import React, { useMemo } from "react";
import { Box, Chip, Typography } from "@mui/material";
import { flexRender, Table } from "@tanstack/react-table";

type Props<T> = {
  table: Table<T>;
  // your dynamic columns metadata (optional, but you already have it)
  columnsMeta?: Array<{ columnName: string; title?: string }>;
};

export function FilterChipsBar<T>({ table, columnsMeta }: Props<T>) {
  const filters = table.getState().columnFilters;

  const chips = useMemo(() => {
    const out: Array<{
      key: string;
      columnId: string;
      value: string;
      label: string;
    }> = [];

    for (const f of filters) {
      const colId = f.id;

      // 1) Try your meta titles (best human label)
      const metaTitle =
        columnsMeta?.find((c) => c.columnName === colId)?.title;

      // 2) Else derive from TanStack column header
      const col = table.getColumn(colId);
      const headerLabel =
        metaTitle ??
        (col
          ? String(
              flexRender(col.columnDef.header, {
                table,
                header: undefined as any,
                column: col,
              } as any)
            )
          : colId);

      const vals = Array.isArray(f.value) ? (f.value as string[]) : [];
      for (const v of vals) {
        out.push({
          key: `${colId}:${v}`,
          columnId: colId,
          value: v,
          label: `${headerLabel}: ${v}`,
        });
      }
    }

    return out;
  }, [filters, table, columnsMeta]);

  if (!chips.length) return null;

  const removeOne = (columnId: string, value: string) => {
    const col = table.getColumn(columnId);
    if (!col) return;

    const current = (col.getFilterValue() as string[] | undefined) ?? [];
    const next = current.filter((x) => x !== value);
    col.setFilterValue(next.length ? next : undefined);
  };

  const clearAll = () => table.resetColumnFilters();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        px: 1,
        py: 0.75,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        backgroundColor: "background.paper",
        mb: 1,
      }}
    >
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
        Filters:
      </Typography>

      {chips.map((c) => (
        <Chip
          key={c.key}
          size="small"
          label={c.label}
          onDelete={() => removeOne(c.columnId, c.value)}
        />
      ))}

      <Chip
        size="small"
        variant="outlined"
        label="Clear all"
        onClick={clearAll}
        sx={{ ml: "auto" }}
      />
    </Box>
  );
}
